import React, { useRef, forwardRef, useImperativeHandle } from 'react'

import { Button } from '@mui/material'
import { AttachmentType } from 'types/graphql'

import useUploadFile from 'src/hooks/requests/useUploadFile'

export type FileUploadButtonProps = {
  //component re-renders when switching tasks, pass a unique key
  //to the component that changes when you switch tasks.
  //React uses the key prop to understand which items have changed,
  // are added, or are removed and this can trigger a re-render of the component.
  key?: string // for rerender
  button?: React.ReactNode
  attachables?: { id: string; type: AttachmentType }[]
  refetchAttachments?: () => void
  accept?: string
  alternativeOnSave?: (file: File) => void
  multiple?: boolean
}

// Define the ref type
export type FileUploadButtonRef = {
  openFileDialog: () => void
}

const FileUploadButton = forwardRef<FileUploadButtonRef, FileUploadButtonProps>(
  (
    {
      key, // for rerender
      button,
      attachables,
      refetchAttachments,
      accept,
      alternativeOnSave,
      multiple = false,
    },
    ref
  ) => {
    const fileInput = useRef<HTMLInputElement>(null)
    const uploadFile = useUploadFile()

    // Expose the openFileDialog method to the parent component
    useImperativeHandle(ref, () => ({
      openFileDialog: () => {
        if (fileInput.current) {
          fileInput.current.click()
        }
      },
    }))

    const handleButtonClick = () => {
      if (fileInput.current) {
        fileInput.current.click()
      }
    }

    const handleDivClick = (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
      event.nativeEvent.stopImmediatePropagation()
    }

    const handleFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const files = event.target.files
      if (files && files.length > 0) {
        // Convert FileList to array for easier handling
        const fileArray = Array.from(files)

        // Process files in batches of 5
        const batchSize = 5
        for (let i = 0; i < fileArray.length; i += batchSize) {
          const batch = fileArray.slice(i, i + batchSize)

          // Process current batch in parallel
          const uploadPromises = batch.map((file) => {
            return new Promise((resolve, reject) => {
              try {
                const uploadTask = alternativeOnSave
                  ? alternativeOnSave(file)
                  : uploadFile(file, attachables)

                // Handle both synchronous results and promises
                Promise.resolve(uploadTask)
                  .then((response) => {
                    // uploadFile already handles success states internally
                    resolve({
                      file,
                      response,
                      success: true,
                    })
                  })
                  .catch((error) => {
                    // No need to log or update status here as useUploadFile already does that
                    reject({
                      file,
                      error,
                      success: false,
                    })
                  })
              } catch (e) {
                // This is still needed for synchronous errors from alternativeOnSave
                console.error(`Error processing file ${file.name}:`, e)
                reject({
                  file,
                  error: e,
                  success: false,
                })
              }
            })
          })

          // Wait for current batch to complete before processing next batch
          await Promise.allSettled(uploadPromises)
        }

        if (refetchAttachments) {
          refetchAttachments()
        }
      }
      // Reset the value of the file input so can upload the same file if deleted right before
      event.target.value = ''
    }

    return (
      <div onClick={handleDivClick}>
        <input
          type="file"
          ref={fileInput}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept={accept || '*/*'}
          multiple={multiple}
        />
        {button ? (
          React.cloneElement(button, { onClick: handleButtonClick })
        ) : (
          <Button
            color="primary"
            variant="contained"
            onClick={handleButtonClick}
          >
            Upload File
          </Button>
        )}
      </div>
    )
  }
)

// Add display name for better debugging
FileUploadButton.displayName = 'FileUploadButton'

export default FileUploadButton
